/**
 * Circular full-page theme reveal (View Transitions API).
 * Falls back to instant switch when unsupported or reduced motion is on.
 */

export function getTheme() {
    return document.body.classList.contains('dark') ? 'dark' : 'light';
}

export function syncThemeIcons(theme) {
    const isDark = theme === 'dark';

    document.querySelectorAll('[id="theme-light"]').forEach((el) => {
        el.classList.toggle('hidden', !isDark);
    });

    document.querySelectorAll('[id="theme-dark"]').forEach((el) => {
        el.classList.toggle('hidden', isDark);
    });
}

export function setTheme(mode) {
    const theme = mode === 'light' ? 'light' : 'dark';

    document.body.classList.toggle('dark', theme === 'dark');
    syncThemeIcons(theme);

    document.dispatchEvent(new CustomEvent('theme:changed', { detail: { theme } }));
}

function saveThemeToServer(theme) {
    fetch('/theme/switch', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ theme }),
        credentials: 'same-origin',
    }).catch((error) => {
        console.error('Error switching theme:', error);
    });
}

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getRevealOrigin(event, fallbackEl) {
    if (event && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
        return { x: event.clientX, y: event.clientY };
    }

    if (fallbackEl) {
        const rect = fallbackEl.getBoundingClientRect();
        return {
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
        };
    }

    return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
}

function revealRadius(x, y) {
    return Math.hypot(
        Math.max(x, window.innerWidth - x),
        Math.max(y, window.innerHeight - y),
    );
}

/**
 * @param {MouseEvent|KeyboardEvent|null} event
 * @param {HTMLElement|null} originEl
 * @returns {'light'|'dark'}
 */
export function toggleTheme(event = null, originEl = null) {
    const next = getTheme() === 'light' ? 'dark' : 'light';
    const apply = () => {
        setTheme(next);
        saveThemeToServer(next);
    };

    if (prefersReducedMotion() || typeof document.startViewTransition !== 'function') {
        apply();
        return next;
    }

    const { x, y } = getRevealOrigin(event, originEl);
    const endRadius = revealRadius(x, y);

    const transition = document.startViewTransition(apply);

    transition.ready
        .then(() => {
            document.documentElement.animate(
                {
                    clipPath: [
                        `circle(0px at ${x}px ${y}px)`,
                        `circle(${endRadius}px at ${x}px ${y}px)`,
                    ],
                },
                {
                    duration: 580,
                    easing: 'cubic-bezier(0.76, 0.32, 0.29, 0.99)',
                    pseudoElement: '::view-transition-new(root)',
                },
            );
        })
        .catch(() => {});

    transition.finished
        .then(() => syncThemeIcons(next))
        .catch(() => {});

    return next;
}
